import React, { useState, useEffect } from 'react';
import { Search, Filter, Plus, Edit, Trash2, Eye, Bell, Send, Clock, CheckCircle } from 'lucide-react';
import { adminService } from '../services/adminService';
import toast from 'react-hot-toast';

const Notifications = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedType, setSelectedType] = useState('all');
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showSendForm, setShowSendForm] = useState(false);
  const [sendForm, setSendForm] = useState({
    title: '',
    message: '',
    type: 'admin',
    targetUsers: 'all'
  });
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [viewNotification, setViewNotification] = useState(null);

  // Fetch notifications on component mount
  useEffect(() => {
    fetchNotifications();
  }, []);

  const fetchNotifications = async () => {
    try {
      setLoading(true);
      const [userNotifs, broadcastNotifs] = await Promise.all([
        adminService.getNotifications(),
        adminService.getBroadcastNotifications()
      ]);
      // Mark broadcasts for UI
      const broadcasts = Array.isArray(broadcastNotifs)
        ? broadcastNotifs.map(n => ({ ...n, is_broadcast: true }))
        : [];
      const all = [...(userNotifs || []), ...broadcasts];
      all.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
      setNotifications(all);
      setError(null);
      console.log('Notifications loaded:', all.length);
    } catch (err) {
      console.error('Error fetching notifications:', err);
      setNotifications([]);
      setError(err.message);
      toast.error('Failed to fetch notifications');
    } finally {
      setLoading(false);
    }
  };

  // Mock data for demonstration
  const mockNotifications = [
    {
      id: 1,
      title: 'Welcome to our platform!',
      message: 'Thank you for joining us. We hope you enjoy your experience.',
      type: 'admin',
      is_read: false,
      created_at: '2024-01-20T10:00:00.000Z',
      user_id: 1,
    },
    {
      id: 2,
      title: 'New campaign available',
      message: 'A new campaign has been posted. Check it out!',
      type: 'system',
      is_read: true,
      created_at: '2024-01-19T15:30:00.000Z',
      user_id: 2,
    },
  ];

  // Use real data if available, otherwise fallback to mock
  const displayNotifications = notifications.length > 0 ? notifications : mockNotifications;
  
  const filteredNotifications = displayNotifications.filter(notification => {
    const matchesSearch = (
      (notification.title && notification.title.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (notification.message && notification.message.toLowerCase().includes(searchTerm.toLowerCase()))
    );
    const matchesType = selectedType === 'all' || notification.type === selectedType;
    return matchesSearch && matchesType;
  });

  const getTypeBadge = (type) => {
    const typeConfig = {
      admin: 'bg-blue-100 text-blue-800',
      system: 'bg-green-100 text-green-800',
      campaign: 'bg-purple-100 text-purple-800',
      payment: 'bg-orange-100 text-orange-800',
    };
    return (
      <span className={`px-2 py-1 text-xs font-medium rounded-full ${typeConfig[type] || 'bg-gray-100 text-gray-800'}`}>
        {type || 'Unknown'}
      </span>
    );
  };

  const getReadStatusBadge = (isRead) => {
    return (
      <span className={`px-2 py-1 text-xs font-medium rounded-full ${isRead ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}`}>
        {isRead ? 'Read' : 'Unread'}
      </span>
    );
  };

  const handleSendNotification = async () => {
    try {
      let res;
      if (sendForm.targetUsers === 'all') {
        res = await adminService.post('/broadcast-notifications', {
          title: sendForm.title,
          message: sendForm.message,
          type: sendForm.type,
          target: 'all',
        });
      } else if (['brands', 'influencers', 'premium'].includes(sendForm.targetUsers)) {
        res = await adminService.post('/broadcast-notifications', {
          title: sendForm.title,
          message: sendForm.message,
          type: sendForm.type,
          target: 'role',
          data: { role: sendForm.targetUsers },
        });
      } else if (Array.isArray(sendForm.userIds) && sendForm.userIds.length > 0) {
        // Individual users via FCM
        res = await adminService.post('/admin/push-notification', {
          title: sendForm.title,
          body: sendForm.message,
          type: sendForm.type,
          targetType: 'individual',
          targetValue: sendForm.userIds,
        });
      } else {
        toast.error('Please select users to notify.');
        return;
      }
      toast.success('Notification sent successfully!');
      setShowSendForm(false);
      setSendForm({ title: '', message: '', type: 'admin', targetUsers: 'all' });
      fetchNotifications();
    } catch (error) {
      if (error?.response?.data?.error) {
        toast.error(`Failed: ${error.response.data.error}`);
      } else {
        toast.error('Failed to send notification');
      }
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setSendForm(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleViewNotification = (notification) => {
    setViewNotification(notification);
    setViewDialogOpen(true);
  };

  const closeViewDialog = () => {
    setViewDialogOpen(false);
    setViewNotification(null);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Notifications</h1>
          <p className="text-gray-600">
            {error ? 'Showing demo data (API connection failed)' : 'Manage platform notifications'}
          </p>
        </div>
        <button 
          onClick={() => setShowSendForm(true)}
          className="btn-primary flex items-center"
        >
          <Send className="h-4 w-4 mr-2" />
          Send Notification
        </button>
      </div>

      {/* Send Notification Form */}
      {showSendForm && (
        <div className="card p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Send New Notification</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Title</label>
              <input
                type="text"
                name="title"
                value={sendForm.title}
                onChange={handleInputChange}
                placeholder="Notification title"
                className="input-field w-full"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Type</label>
              <select
                name="type"
                value={sendForm.type}
                onChange={handleInputChange}
                className="input-field w-full"
              >
                <option value="admin">Admin</option>
                <option value="system">System</option>
                <option value="campaign">Campaign</option>
                <option value="payment">Payment</option>
              </select>
            </div>
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-2">Message</label>
              <textarea
                name="message"
                value={sendForm.message}
                onChange={handleInputChange}
                placeholder="Notification message"
                rows={3}
                className="input-field w-full"
              />
            </div>
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-2">Target Users</label>
              <select
                name="targetUsers"
                value={sendForm.targetUsers}
                onChange={handleInputChange}
                className="input-field w-full"
              >
                <option value="all">All Users</option>
                <option value="brands">Brands Only</option>
                <option value="influencers">Influencers Only</option>
                <option value="premium">Premium Users Only</option>
              </select>
            </div>
          </div>
          <div className="flex gap-3 mt-4">
            <button
              onClick={handleSendNotification}
              className="btn-primary"
            >
              <Send className="h-4 w-4 mr-2" />
              Send Notification
            </button>
            <button
              onClick={() => setShowSendForm(false)}
              className="btn-secondary"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Loading State */}
      {loading && (
        <div className="card p-12 text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading notifications...</p>
        </div>
      )}

      {/* Error State */}
      {error && !loading && (
        <div className="card p-6 mb-6">
          <div className="text-center">
            <p className="text-red-600 mb-4">{error}</p>
            <p className="text-gray-600 mb-4">Showing demo data below. Click retry to attempt fetching real data again.</p>
            <button onClick={fetchNotifications} className="btn-primary">
              Retry
            </button>
          </div>
        </div>
      )}

      {/* Filters and Search */}
      <div className="card p-6">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search notifications..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="input-field pl-10"
              />
            </div>
          </div>
          <div className="flex gap-3">
            <select
              value={selectedType}
              onChange={(e) => setSelectedType(e.target.value)}
              className="input-field"
            >
              <option value="all">All Types</option>
              <option value="admin">Admin</option>
              <option value="system">System</option>
              <option value="campaign">Campaign</option>
              <option value="payment">Payment</option>
            </select>
            <button className="btn-secondary flex items-center">
              <Filter className="h-4 w-4 mr-2" />
              More Filters
            </button>
          </div>
        </div>
      </div>

      {/* Notifications List */}
      {!loading && (
        <div className="space-y-4">
          {filteredNotifications.map((notification) => (
            <div key={notification.id} className="card p-6 hover:shadow-md transition-shadow">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <h3 className="text-lg font-semibold text-gray-900">
                      {notification.title || 'No Title'}
                    </h3>
                    {getTypeBadge(notification.type)}
                    {getReadStatusBadge(notification.is_read)}
                    {notification.is_broadcast && (
                      <span className="px-2 py-1 text-xs font-medium rounded-full bg-indigo-100 text-indigo-800 ml-2">
                        Broadcast
                      </span>
                    )}
                  </div>
                  <p className="text-gray-600 mb-3">
                    {notification.message || 'No message content'}
                  </p>
                  <div className="flex items-center gap-4 text-sm text-gray-500">
                    <div className="flex items-center gap-1">
                      <Clock className="h-4 w-4" />
                      {notification.created_at ? new Date(notification.created_at).toLocaleDateString() : 'N/A'}
                    </div>
                    {notification.user_id && (
                      <span>User ID: {notification.user_id}</span>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    className="text-primary-600 hover:text-primary-900 p-1"
                    onClick={() => handleViewNotification(notification)}
                  >
                    <Eye className="h-4 w-4" />
                  </button>
                  <button className="text-blue-600 hover:text-blue-900 p-1">
                    <Edit className="h-4 w-4" />
                  </button>
                  <button className="text-red-600 hover:text-red-900 p-1">
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Empty State */}
      {!loading && filteredNotifications.length === 0 && (
        <div className="card p-12 text-center">
          <div className="mx-auto h-12 w-12 text-gray-400 mb-4">
            <Bell className="h-12 w-12" />
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">No notifications found</h3>
          <p className="text-gray-500 mb-6">
            {searchTerm || selectedType !== 'all' 
              ? 'Try adjusting your search or filter criteria.'
              : 'No notifications have been sent yet.'
            }
          </p>
          <button 
            onClick={() => setShowSendForm(true)}
            className="btn-primary"
          >
            <Send className="h-4 w-4 mr-2" />
            Send First Notification
          </button>
        </div>
      )}

      {/* Pagination */}
      {!loading && filteredNotifications.length > 0 && (
        <div className="card p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-700">
                Showing <span className="font-medium">1</span> to <span className="font-medium">{filteredNotifications.length}</span> of{' '}
                <span className="font-medium">{filteredNotifications.length}</span> results
              </p>
            </div>
            <div>
              <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                <button className="btn-secondary rounded-l-md">Previous</button>
                <button className="btn-secondary rounded-r-md">Next</button>
              </nav>
            </div>
          </div>
        </div>
      )}

      {/* View Notification Dialog */}
      {viewDialogOpen && viewNotification && (
        <div className="fixed inset-0 flex items-center justify-center z-50 bg-gray-900 bg-opacity-40">
          <div className="bg-white rounded-lg shadow-lg p-8 min-w-[320px] max-w-md">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              {viewNotification.title || 'No Title'}
            </h3>
            <p className="mb-4 text-gray-700">{viewNotification.message || 'No message content'}</p>
            <div className="mb-2">
              <span className="font-medium">Type:</span> {getTypeBadge(viewNotification.type)}
            </div>
            <div className="mb-2">
              <span className="font-medium">Status:</span> {getReadStatusBadge(viewNotification.is_read)}
            </div>
            <div className="mb-2 text-sm text-gray-500">
              <Clock className="inline h-4 w-4 mr-1" />
              {viewNotification.created_at ? new Date(viewNotification.created_at).toLocaleString() : 'N/A'}
            </div>
            {viewNotification.user_id && (
              <div className="mb-2 text-sm text-gray-500">
                User ID: {viewNotification.user_id}
              </div>
            )}
            {viewNotification.is_broadcast && (
              <div className="mb-2">
                <span className="px-2 py-1 text-xs font-medium rounded-full bg-indigo-100 text-indigo-800">
                  Broadcast Notification
                </span>
              </div>
            )}
            <div className="flex justify-end mt-6">
              <button className="btn-secondary px-6 py-2" onClick={closeViewDialog}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Notifications;
